import { defineStore } from "pinia";
import axios from "axios";
import apiClient from "@/api/client";
import { userManager } from "@/auth/oidc";
import { logAuditEvent, AUDIT_CODES } from "@/api/audit";

function storageKeys(storage) {
  const keys = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key) keys.push(key);
  }
  return keys;
}

function clearOidcStorage() {
  for (const storage of [sessionStorage, localStorage]) {
    storageKeys(storage).forEach((key) => {
      if (key.includes("oidc") || key.includes("oidc-client")) storage.removeItem(key);
    });
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    /** From /api/auth/me - source of truth for customerId/isSystemAdmin (token may not have claim) */
    authMe: null,
    /** When admin is impersonating, show normal app instead of redirecting to admin */
    impersonating: false,
    status: "checking",
    invalidating: false,
    sessionGeneration: 0,
  }),
  getters: {
    isAuthenticated: (s) =>
      s.status === "authenticated" &&
      !!s.user?.access_token &&
      !!s.user?.profile?.sub &&
      !s.user.expired,
    customerId: (s) => {
      // Prefer backend API (source of truth)
      if (s.authMe != null) return String(s.authMe.customerId);
      if (!s.user?.id_token) return null;
      try {
        const payload = JSON.parse(atob(s.user.id_token.split(".")[1]));
        return payload.customer_id != null ? String(payload.customer_id) : null;
      } catch {
        return null;
      }
    },
    isSystemAdmin: (s, getters) => (getters?.customerId ?? null) === "0",
    /** Can access admin routes: CustomerId=0 OR email is info@waywyser.com (fallback) */
    canAccessAdmin: (s, getters) => {
      if (getters?.isSystemAdmin) return true;
      const email = s.user?.profile?.email;
      const adminEmail = "info@waywyser.com";
      return email === adminEmail || (Array.isArray(email) && email.includes(adminEmail));
    },
  },
  actions: {
  async init() {
  this.status = "checking";
  let user;
  try {
    user = await userManager.getUser();
  } catch (err) {
    console.error("[AuthStore] Stored session could not be read:", err);
    await this.invalidateSession({ reason: "invalid_storage" });
    return;
  }

  if (user?.access_token && user?.profile?.sub && !user.expired) {
    this.user = user;
    axios.defaults.headers.common.Authorization = `Bearer ${user.access_token}`;

    try {
      const me = await apiClient.get("/auth/me", { skipAuthRedirect: true });
      this.authMe = me.data && typeof me.data === "object" ? me.data : null;
      if (!this.authMe) throw new Error("Invalid auth/me response");
    } catch (err) {
      await this.invalidateSession({ reason: "session_validation_failed" });
      return;
    }

    // ✅ Fetch latest AccessRights from backend
    try {
      const res = await apiClient.get("/AccessRights");
      localStorage.setItem("claims", JSON.stringify(res.data || []));
    } catch (err) {
    }

    // ✅ Fetch auth/me for customerId & isSystemAdmin (backend is source of truth)
    try {
      const me = await apiClient.get("/auth/me", { skipAuthRedirect: true });
      // Guard against a non-JSON body (e.g. a misroute returning index.html) being
      // stored as a logged-in session — that silently disables module gating.
      this.authMe = me.data && typeof me.data === "object" ? me.data : null;
    } catch (err) {
      await this.invalidateSession({ reason: "session_validation_failed" });
      return;
    }

    // ✅ Check impersonation status (don't redirect to admin when impersonating).
    // Must be independent of authMe.isSystemAdmin: while impersonating, /api/auth/me
    // now reports the impersonated customer (isSystemAdmin=false), but the
    // impersonation-status endpoint authorizes against the REAL signed-in admin, so
    // it still reports the active impersonation correctly.
    try {
      const imp = await apiClient.get("/admin/impersonation-status");
      this.impersonating = !!imp.data?.impersonating;
    } catch {
      this.impersonating = false;
    }

    this.status = "authenticated";

  } else {
    this.user = null;
    this.authMe = null;
    this.impersonating = false;
    this.status = "unauthenticated";
    delete axios.defaults.headers.common.Authorization;
  }
},
    async invalidateSession({ reason = "session_invalid" } = {}) {
      if (this.invalidating) return;
      this.invalidating = true;
      try {
        this.sessionGeneration += 1;
        this.user = null;
        this.authMe = null;
        this.impersonating = false;
        this.status = "unauthenticated";
        delete axios.defaults.headers.common.Authorization;
        localStorage.removeItem("claims");
        await Promise.resolve(userManager.removeUser()).catch(() => {});
        clearOidcStorage();
      } finally {
        this.invalidating = false;
      }
    },
    async recoverToLogin(reason = "session_invalid", returnPath = "/") {
      await this.invalidateSession({ reason });
      try {
        const authority = import.meta.env.VITE_AUTHORITY || "https://localhost:7188";
        await fetch(`${authority}/api/auth/clear-session`, {
          method: "POST",
          credentials: "include",
        });
      } catch {}
      return userManager.signinRedirect({ state: returnPath, prompt: "login" });
    },
    async login(returnPath = "/") {
      return this.recoverToLogin("login_required", returnPath);
    },
    async completeLogin() {
      const callbackGeneration = this.sessionGeneration;
      try {
      // finishes code→token and returns the user
      const user = await userManager.signinRedirectCallback();
      if (callbackGeneration !== this.sessionGeneration) {
        await Promise.resolve(userManager.removeUser()).catch(() => {});
        throw new Error("Login callback was superseded by session recovery");
      }
      if (!user?.access_token || !user?.profile?.sub || user.expired) {
        throw new Error("Incomplete or expired user returned from OIDC callback");
      }
      axios.defaults.headers.common.Authorization = `Bearer ${user.access_token}`;
      const me = await apiClient.get("/auth/me", { skipAuthRedirect: true });
      this.authMe = me.data && typeof me.data === "object" ? me.data : null;
      if (!this.authMe) throw new Error("Server did not validate the new session");
      this.user = user;
      this.status = "authenticated";
      // the OIDC client parses your "state" for you:
      return user.state || "/";
      } catch (error) {
        await this.invalidateSession({ reason: "callback_failed" });
        // If state mismatch, try to clear and redirect to login
        if (error.message?.includes("state") || error.message?.includes("No matching")) {
          await this.invalidateSession({ reason: "callback_state_mismatch" });
        }
        throw error;
      }
    },
    async logout() {
      try {
        await logAuditEvent({
          auditCode: AUDIT_CODES.USER_LOGOUT,
          annotation: "User Logout",
          entityType: "Authentication",
        });
      } catch {}

      // Clear server-side Identity cookie first
      try {
        const authority = import.meta.env.VITE_AUTHORITY || "https://localhost:7188";
        await fetch(`${authority}/api/auth/clear-session`, {
          method: "POST",
          credentials: "include",
        });
      } catch (e) {}
      await this.invalidateSession({ reason: "logout" });
      try {
        return await userManager.signoutRedirect();
      } catch {
        return this.recoverToLogin("logout_failed");
      }
    },
    async completeLogout() {
      await userManager.signoutRedirectCallback().catch(() => {});
      await this.invalidateSession({ reason: "logout_complete" });
    },
    /** Fetch auth/me from backend (call after login to get customerId/isSystemAdmin) */
    async fetchAuthMe() {
      if (!this.user?.access_token) return null;
      try {
        const me = await apiClient.get("/auth/me", { skipAuthRedirect: true });
        this.authMe = me.data && typeof me.data === "object" ? me.data : null;
        if (!this.authMe) throw new Error("Invalid auth/me response");
        return this.authMe;
      } catch {
        await this.invalidateSession({ reason: "session_validation_failed" });
        return null;
      }
    },
  },
});
